import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import NotificationBanner from '../../components/ui/NotificationBanner';
import Footer from '../../components/layout/Footer';
import UserManagement from './UserManagement';
import TeacherManagement from './TeacherManagement';
import LearningSessionManagement from './LearningSessionManagement';
import Analytics from './Analytics';
import { FaUsers, FaChalkboardTeacher, FaGraduationCap, FaChartBar, FaArrowLeft, FaHome } from 'react-icons/fa';

const AdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('users');

  const tabs = [
    { id: 'users', label: 'Users', icon: FaUsers },
    { id: 'teachers', label: 'Teachers', icon: FaChalkboardTeacher },
    { id: 'sessions', label: 'Learning Sessions', icon: FaGraduationCap },
    { id: 'analytics', label: 'Analytics', icon: FaChartBar },
  ];

  // Handle tab changes from URL parameters
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabs.find(tab => tab.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagement />;
      case 'teachers':
        return <TeacherManagement />;
      case 'sessions':
        return <LearningSessionManagement />;
      case 'analytics':
        return <Analytics />;
      default:
        return <UserManagement />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - NexGen Learning</title>
        <meta name="description" content="Admin dashboard for managing users and learning sessions" />
      </Helmet>
      
      <Header />
      <NotificationBanner />
      
      <main className="min-h-screen bg-gray-50 pt-20">
        {/* Back to Home Section */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                >
                  <FaArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-700 transition-colors"
                >
                  <FaHome className="mr-2 h-4 w-4" />
                  Home
                </Link>
              </div>
              <div className="flex items-center space-x-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  Admin Dashboard
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="mt-1 text-sm text-gray-500">Manage users, teachers, and learning sessions</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  Admin
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          {renderTabContent()}
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default AdminDashboard; 